import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'client',
    status: 'active'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.getUsers({ role: filterRole, search: searchTerm });
      const rawUsers = res.users || res.data || [];
      const mapped = rawUsers.map(u => ({
        id: u._id || u.id,
        name: u.name || '',
        email: u.email || '',
        phone: u.phone || '',
        role: u.role || 'client',
        status: u.isActive !== false ? 'active' : 'inactive',
        joinDate: u.createdAt ? new Date(u.createdAt).toISOString().split('T')[0] : 'N/A'
      }));
      setUsers(mapped);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(err.message || 'Failed to load users from server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchUsers();
  }, [authSession?.token, navigate, filterRole]);

  const handleAdd = () => {
    setFormData({ name: '', email: '', password: '', phone: '', role: 'client', status: 'active' });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      phone: user.phone,
      role: user.role,
      status: user.status
    });
    setEditingId(user.id || user._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await adminApi.deleteUser(id);
        setUsers(prev => prev.filter(u => u.id !== id && u._id !== id));
        fetchUsers();
      } catch (err) {
        alert(err.message || 'Failed to delete user');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || (!editingId && !formData.password)) {
      alert('Please fill all required fields (Name, Email, and Password for new user)');
      return;
    }

    try {
      if (editingId) {
        const updatePayload = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          role: formData.role,
          isActive: formData.status === 'active'
        };
        if (formData.password && formData.password.trim().length > 0) {
          updatePayload.password = formData.password;
        }
        await adminApi.updateUser(editingId, updatePayload);
      } else {
        await adminApi.createUser({
          name: formData.name,
          email: formData.email,
          password: formData.password || 'User@123',
          phone: formData.phone,
          role: formData.role,
          isActive: formData.status === 'active'
        });
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', email: '', password: '', phone: '', role: 'client', status: 'active' });
      fetchUsers();
    } catch (err) {
      alert(err.message || 'Failed to save user');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', email: '', password: '', phone: '', role: 'client', status: 'active' });
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterRole]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const stats = {
    total: users.length,
    therapists: users.filter(u => u.role === 'physiotherapist').length,
    clients: users.filter(u => u.role === 'client').length,
    active: users.filter(u => u.status === 'active').length
  };

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div style={{ marginLeft: '280px', padding: '30px', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ color: '#333', marginBottom: '10px' }}>User Management</h1>
          <p style={{ color: '#666', fontSize: '14px' }}>Manage all users, therapists, and clients in the system</p>
        </div>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>👥 Total Users</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>{stats.total}</div>
          </div>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>🩺 Therapists</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#667eea' }}>{stats.therapists}</div>
          </div>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>👤 Clients</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#764ba2' }}>{stats.clients}</div>
          </div>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>✓ Active</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#28a745' }}>{stats.active}</div>
          </div>
        </div>

        {/* Search and Filter */}
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 15px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                flex: 1,
                minWidth: '200px',
                fontSize: '14px'
              }}
            />
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              style={{
                padding: '10px 15px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            >
              <option value="all">All Roles</option>
              <option value="physiotherapist">Therapists</option>
              <option value="client">Clients</option>
              {/* <option value="admin">Admins</option> */}
            </select>
            <button
              onClick={handleAdd}
              style={{
                padding: '10px 20px',
                backgroundColor: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              + Add User
            </button>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '10px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginBottom: '15px', color: '#333' }}>{editingId ? 'Edit User' : 'Add New User'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Full name"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Email address"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {!editingId && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Password (at least 6 characters)"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Phone number"
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Role</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff' }}
                  >
                    <option value="client">Client</option>
                    <option value="physiotherapist">Therapist</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500', color: '#333' }}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff' }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#667eea',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {editingId ? 'Update User' : 'Add User'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        <div style={{ backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '14px' }}>Name</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '14px' }}>Email</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '14px' }}>Phone</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '14px' }}>Role</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '14px' }}>Status</th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#333', fontSize: '14px' }}>Join Date</th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', color: '#333', fontSize: '14px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #dee2e6', '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <td style={{ padding: '15px', color: '#333', fontSize: '14px', fontWeight: '500' }}>{user.name}</td>
                    <td style={{ padding: '15px', color: '#666', fontSize: '14px' }}>{user.email}</td>
                    <td style={{ padding: '15px', color: '#666', fontSize: '14px' }}>{user.phone}</td>
                    <td style={{ padding: '15px', fontSize: '14px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        backgroundColor: user.role === 'physiotherapist' ? '#e7f3ff' : user.role === 'client' ? '#e8f5e9' : '#f3e5f5',
                        color: user.role === 'physiotherapist' ? '#0066cc' : user.role === 'client' ? '#2e7d32' : '#7b1fa2',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {user.role === 'physiotherapist' ? '🩺 Therapist' : user.role === 'client' ? '👤 Client' : '👨‍💼 Admin'}
                      </span>
                    </td>
                    <td style={{ padding: '15px', fontSize: '14px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        backgroundColor: user.status === 'active' ? '#d4edda' : user.status === 'inactive' ? '#f8d7da' : '#e2e3e5',
                        color: user.status === 'active' ? '#155724' : user.status === 'inactive' ? '#721c24' : '#383d41',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '15px', color: '#999', fontSize: '14px' }}>{user.joinDate}</td>
                    <td style={{ padding: '15px', textAlign: 'center', fontSize: '14px' }}>
                      <button
                        onClick={() => handleEdit(user)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ffc107',
                          color: '#000',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '5px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredUsers.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              <p style={{ fontSize: '16px' }}>No users found matching filter criteria</p>
            </div>
          )}
          {!loading && filteredUsers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredUsers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={(newSize) => {
                setItemsPerPage(newSize);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AdminUsersPage;
